<template>
  <div class="inline-flex items-center gap-1">
    <item-state
      v-if="mission.misActifOn"
      :waitingMode="mission.gliderWaitingMode"
      :alarmStatus="mission.alarmStatusLast"
    />
    <template v-if="showAisIndicator && mission.misActifOn && aisStatus && aisStatus !== 'OFF'">
      <v-icon :color="aisIconColor" :class="{ blink: aisStatus === 'RISK' }" :title="aisIconTitle">mdi-ferry</v-icon>
    </template>
    <v-icon v-if="mission?.isGeoHidden" :title="$t('Pilot.MissionDrawer.GeoHidden')">mdi-lock</v-icon>
  </div>
</template>
<script lang="ts">
import { AloMission } from '@/models/alo/AloMission'
import ItemState from '@/components/shared/ItemState.vue'
import Vue from 'vue'
import Component from 'vue-class-component'
import { SecurityItemsKeys } from '@/enum/SecurityItems'
import mission from '@/api/mission'
import { Watch } from 'vue-property-decorator'

// Missions dont le glider state a déjà été demandé : plusieurs MissionState affichent la même
// mission (liste, onglets, bandeau) — une seule requête IRIS par mission.
const gliderStateRequested = new Set<number>()

@Component({
  name: 'MissionState',
  props: {
    mission: AloMission,
  },
  components: { ItemState },
})
export default class MissionState extends Vue {
  mission!: AloMission

  get missionSecurity() {
    // Lire la security de CETTE mission (la prop), pas celle du detail courant.
    // L'ancien code lisait `currentDetailMission.mission.security` ce qui faisait
    // que toutes les lignes de liste reflétaient l'état du dernier détail ouvert
    // (icône AIS contaminée cross-mission). Bug pré-existant masqué tant que
    // applyAisSecurityOverride denyait tout faute d'enseignesById chargé.
    return this.mission.security
  }

  get showAisIndicator(): boolean {
    return this.missionSecurity[SecurityItemsKeys.PILOT_DETAIL_ANTICOLLISION]?.visible ?? false
  }

  /**
   * Module AIS activé ? Runtime du planeur d'abord (mis à jour en temps réel par WobGliderModConf),
   * sinon config AIS chargée ; null = inconnu.
   */
  get aisModuleEnabled(): boolean | null {
    const runtimeEnabled = this.mission.gliderRuntime?.modules?.AIS
    if (typeof runtimeEnabled === 'boolean') return runtimeEnabled
    const confEnabled =
      this.$store.myGetters.originalAisConfigs[this.mission.misId!]?.mod_settings?.EnabledMOD?.['module-AIS']
    return typeof confEnabled === 'boolean' ? confEnabled : null
  }

  /**
   * Icône seulement module AIS activé (parité v2, BK-098) : sans lui, le statut servi avec la mission
   * (MIS_AIS_STATUS) peut être périmé, le back ne le remet pas à zéro. Mission en cours seulement
   * (template). `Unknown` (statut illisible côté SELIA) → NO_DATA, jamais « aucun danger » (RG-AIS-208).
   */
  get aisStatus(): string | null {
    if (this.aisModuleEnabled !== true) return null
    const status: string | null =
      this.$store.myGetters.originalAisConfigs[this.mission.misId!]?.mod_settings?.ANTICOLL?.module_status ??
      this.mission.aisStatus
    return status === 'Unknown' ? 'NO_DATA' : status
  }

  /**
   * Liste : l'état des modules (gliderRuntime) n'était chargé qu'à l'ouverture du détail, et
   * l'icône restait celle de MIS_AIS_STATUS même module coupé (BK-098). Chargé une fois par
   * mission en cours qui afficherait une icône ; ensuite `canQueryIrisAis` lit le vrai statut.
   */
  get needsGliderState(): boolean {
    return (
      this.showAisIndicator &&
      this.mission.misId != null &&
      this.mission.misActifOn &&
      !this.mission.gliderRuntime &&
      !!this.mission.aisStatus &&
      this.mission.aisStatus !== 'OFF'
    )
  }

  @Watch('needsGliderState', { immediate: true })
  onNeedsGliderStateChange(needed: boolean) {
    if (!needed || gliderStateRequested.has(this.mission.misId!)) return
    gliderStateRequested.add(this.mission.misId!)
    this.$store.myActions.MISSION_IRIS_GLIDER_STATE_REQUEST(this.mission.misId!).catch(() => {
      gliderStateRequested.delete(this.mission.misId!)
    })
  }

  get aisIconColor(): string {
    if (this.aisStatus === 'RISK') return 'red'
    if (this.aisStatus === 'NO_DATA') return 'orange'
    return 'green'
  }

  get aisIconTitle(): string {
    if (this.aisStatus === 'RISK') return this.$t('Pilot.Anticollision.StatusRisk') as string
    if (this.aisStatus === 'NO_DATA') return this.$t('Pilot.Anticollision.StatusNoData') as string
    return this.$t('Pilot.Anticollision.StatusNoRisk') as string
  }

  get canQueryIrisAis(): boolean {
    return (
      (this.showAisIndicator &&
        this.mission.misId != null &&
        this.mission.misActifOn &&
        this.mission.gliderRuntime?.modules?.AIS === true) ||
      false
    )
  }

  @Watch('canQueryIrisAis')
  onCanQueryIrisAisChange(newValue: boolean) {
    if (newValue && !this.$store.myGetters.originalAisConfigs[this.mission.misId!]) {
      this.$store.myActions.MISSION_IRIS_AIS_REQUEST(this.mission.misId!)
    }
  }

  created() {
    if (this.canQueryIrisAis) {
      this.$store.myActions.MISSION_IRIS_AIS_REQUEST(this.mission.misId!)
    }
  }
}
</script>
<style scoped>
@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.2;
  }
}
.blink {
  animation: blink 1.5s ease-in-out infinite;
}
</style>
